import { useVerify } from '@/api/register/CreateRegister'
import useAuthStore from '@/store/useAuthStore'
import { IResponseType } from '@/types/response-type'
import faToEnNum from '@/utils/functions/convertNumbers'
import useEnterKey from '@/utils/functions/useEnterKey'
import Button from '@dgshahr/ui-kit/Button'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import IconPencilOutlineIcon from '../icons/IconPencilOutline'
import OtpInput from './OtpInput'

interface ISendOtpOptions {
  forceResend?: boolean
}

interface IOtpStepProps {
  phoneNumber: string
  handleSendOtp: (options?: ISendOtpOptions) => Promise<void>
  resendLoading: boolean
  goBack: () => void
  remainingTime: number
  formattedTime: string
  onClose: () => void
}

const DEFAULT_OTP_ERROR = 'کد وارد شده صحیح نیست'

function getApiErrorMessage(err: unknown) {
  const data = (err as AxiosError<IResponseType<unknown>>).response?.data

  if (!data) return DEFAULT_OTP_ERROR

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const firstError = data.errors[0]

    if (typeof firstError === 'string' && firstError) {
      return firstError
    }

    if (firstError && typeof firstError === 'object') {
      const nestedMessages = Object.values(firstError).flat()
      const firstMessage = nestedMessages.find(
        (message): message is string =>
          typeof message === 'string' && Boolean(message)
      )

      if (firstMessage) return firstMessage
    }
  }

  return data.message || DEFAULT_OTP_ERROR
}

function OtpStep({
  phoneNumber,
  handleSendOtp,
  resendLoading,
  goBack,
  remainingTime,
  formattedTime,
  onClose
}: IOtpStepProps) {
  const [localOtp, setLocalOtp] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [loadingOtp, setLoadingOtp] = useState(false)
  const [webOtpListenKey, setWebOtpListenKey] = useState(0)

  const { setOtp, setTokens, setLastStep } = useAuthStore()
  const navigate = useNavigate()

  const { mutate: verify } = useVerify({
    data: {
      phone_number: phoneNumber.replace(/\s/g, ''),
      code: localOtp,
      stage: 'mrf'
    }
  })

  const handleOtpChange = (value: string) => {
    setLocalOtp(faToEnNum(value))
    setErrorMessage('')
  }

  const handleResendOtp = async () => {
    try {
      await handleSendOtp({ forceResend: true })
      setLocalOtp('')
      setErrorMessage('')
      setWebOtpListenKey((key) => key + 1)
    } catch {
      // Parent handles error toast
    }
  }

  const handleGoBack = () => {
    setLocalOtp('')
    setErrorMessage('')
    goBack()
  }

  const handleOtpSubmit = () => {
    if (localOtp.length !== 4 || loadingOtp) return

    setLoadingOtp(true)
    setErrorMessage('')

    verify(undefined, {
      onSuccess: (responseData) => {
        setOtp(localOtp)
        toast.success('با موفقیت وارد شدید')
        setTokens(responseData.access_token ?? '')
        setLastStep(responseData.step ?? null)
        setLocalOtp('')
        onClose()
        setLoadingOtp(false)

        const stepPath: Record<number, string> = {
          1: '/merchant-register/category',
          2: '/merchant-register/address',
          3: '/merchant-register/document-upload',
          4: '/merchant-register/contract',
          5: '/merchant-register/status'
        }
        const lastStep = responseData.step

        navigate(stepPath[lastStep] ?? '/merchant-register/info')
      },
      onError: (err) => {
        setLoadingOtp(false)
        setErrorMessage(getApiErrorMessage(err))
      }
    })
  }

  useEnterKey(handleOtpSubmit)

  return (
    <div className="bg-slate-50 p-5">
      <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <p className="flex pb-2 text-sm leading-7 text-slate-600">
          کد پیامک شده به شماره{' '}
          <span
            style={{ direction: 'ltr' }}
            className="px-1 font-medium text-slate-900"
          >
            {phoneNumber}
          </span>
          را وارد کنید
        </p>

        <Button
          onClick={handleGoBack}
          rightIcon={<IconPencilOutlineIcon />}
          className="!h-auto !px-0 !py-0 !text-xs !font-medium !text-primary-600"
          variant="text"
        >
          ویرایش شماره موبایل
        </Button>
      </div>

      <OtpInput
        inputsNumber={4}
        value={localOtp}
        onChange={handleOtpChange}
        listenKey={webOtpListenKey}
        isError={Boolean(errorMessage)}
        errorMessage={errorMessage}
      />

      <div className="mt-2 flex justify-center text-right text-sm text-slate-500">
        {remainingTime > 0 ? (
          <>
            <span className="ml-1 font-medium text-slate-800">
              {formattedTime}
            </span>
            مانده تا ارسال مجدد کد
          </>
        ) : (
          <Button
            variant="secondary"
            size="small"
            isFullWidth
            className="!w-1/2 !bg-primary-500 !text-white hover:!bg-primary-600"
            disabled={resendLoading}
            isLoading={resendLoading}
            onClick={handleResendOtp}
          >
            ارسال مجدد کد
          </Button>
        )}
      </div>

      <Button
        variant="secondary"
        size="medium"
        type="button"
        isLoading={loadingOtp}
        isFullWidth
        className="mt-6 !bg-primary-500 !text-white shadow-sm hover:!bg-primary-600"
        disabled={localOtp.length !== 4 || loadingOtp}
        onClick={handleOtpSubmit}
      >
        ورود
      </Button>
    </div>
  )
}

export default OtpStep
